import { useState } from 'react';
import { useDispatch } from 'react-redux';

import { useApi } from '@/hooks/api';
import { setBannerMessage } from '@/store/actions';
import { CalmError, getCalmErrorOrError } from '@/utils/apiRequest/errors';

export interface InvoiceResponse {
	subtotal: number;
	tax_amount: number;
	total: number;
	discount_amount?: number;
}
type InvoiceDataFunction = (
	covered_lives: number,
	stripe_confirmation_token_id: string,
	coupon?: string,
) => Promise<InvoiceResponse>;
interface InvoicePreviewState {
	loading: boolean;
	error: CalmError | Error | undefined;
}
type InvoicePreviewResponse = [InvoiceDataFunction, InvoicePreviewState];

export const useInvoicePreview = (): InvoicePreviewResponse => {
	const apiRequest = useApi();
	const dispatch = useDispatch();
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<CalmError | Error | undefined>(undefined);

	const getInvoiceData: InvoiceDataFunction = async (
		covered_lives: number,
		stripe_confirmation_token_id: string,
		coupon?: string,
	) => {
		if (loading) return;
		setLoading(true);
		setError(undefined);
		try {
			const response = await apiRequest({
				endpoint: 'b2b/partners/invoice-preview',
				method: 'POST',
				body: {
					covered_lives,
					stripe_confirmation_token_id,
					coupon,
				},
			});
			return response.data;
		} catch (error) {
			setError(getCalmErrorOrError(error));
			// Display error message to retry momentarily because the rate limit could be hit and the request fails
			dispatch(
				setBannerMessage({
					message: 'Failed to retrieve invoice details. Please try again momentarily.',
					isError: true,
					flash: true,
				}),
			);
		} finally {
			setLoading(false);
		}
	};

	return [getInvoiceData, { loading, error }];
};
